import React, { FC, SyntheticEvent, useEffect, useRef, useState } from 'react'
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import { IconMinus, IconPlus } from '@avantstay/backoffice-vectors'

import { HorizontalButtonGroup } from '../HorizontalButtonGroup'
import { Loading } from '../Loading'
import * as S from './ImageZoom.styles'
import { ImageZoomProps } from './ImageZoom.types'

export const ImageZoom: FC<ImageZoomProps> = ({
  src,
  alt,
  testID = 'ImageZoom',
  onError,
  customAction,
  wrapperPaddingX = 0,
  wrapperPaddingY = 0,
}) => {
  const zoomWrapper = useRef<ReactZoomPanPinchRef | null>(null)
  const zoomWrapperElement = zoomWrapper.current

  const [isLoading, setIsLoading] = useState(true)
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | undefined>(
    undefined,
  )

  const handleImageLoad = ({ currentTarget }: SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = currentTarget
    setDimensions({ width, height })
    setIsLoading(false)
  }

  const handleManualZoom = (zoomType: 'in' | 'out') => () => {
    if (!dimensions) return
    if (zoomWrapperElement) {
      const { zoomIn, zoomOut } = zoomWrapperElement
      if (zoomType === 'in') {
        zoomIn()
      } else {
        zoomOut()
      }
    }
  }

  // It's necessary force the first transformation because there is an issue in the library on the centerOnIt property.
  useEffect(() => {
    if (dimensions?.height && zoomWrapperElement) {
      const scale = (window.innerHeight - wrapperPaddingY) / dimensions.height
      const { setTransform } = zoomWrapperElement

      const wrapperCenter = dimensions.width / 2
      const currentImageCenter = (dimensions.width * scale) / 2

      setTransform(
        // This is to start the image in the center, the WrapperPadding properties are used if you have the component inside of a container
        wrapperCenter - currentImageCenter,
        0,
        scale,
        0,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions])

  return dimensions?.width ? (
    <S.ImageZoomContainer data-testid={testID} key={dimensions?.width}>
      <TransformWrapper ref={zoomWrapper} minScale={0.2}>
        <TransformComponent
          wrapperStyle={{
            maxWidth: `calc(100vw - ${wrapperPaddingX}px`,
            maxHeight: `calc(100vh - ${wrapperPaddingY}px)`,
          }}
        >
          <S.Image onLoad={handleImageLoad} src={src} alt={alt} onError={onError} isVisible />
        </TransformComponent>
      </TransformWrapper>

      {!isLoading && (
        <S.ImageZoomController>
          {customAction}
          <HorizontalButtonGroup>
            <HorizontalButtonGroup.Button type="button" onClick={handleManualZoom('out')}>
              <IconMinus />
            </HorizontalButtonGroup.Button>
            <HorizontalButtonGroup.Button type="button" onClick={handleManualZoom('in')}>
              <IconPlus />
            </HorizontalButtonGroup.Button>
          </HorizontalButtonGroup>
        </S.ImageZoomController>
      )}
    </S.ImageZoomContainer>
  ) : (
    <>
      <S.Image
        onLoad={handleImageLoad}
        src={src}
        alt={alt}
        onError={onError}
        isVisible={!!dimensions}
      />
      <S.LoaderContainer>
        <Loading />
      </S.LoaderContainer>
    </>
  )
}
